import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KpiCard } from "@/components/KpiCard";
import { StatusBadge } from "@/components/StatusBadge";
import { formatGBP, formatUKDate } from "@/lib/format";
import { CalendarCheck, Clock, CircleDollarSign, AlertTriangle, ListChecks, CalendarDays } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";

export default function Dashboard() {
  const { user, business } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Good to see you, {user?.first_name}</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl tracking-tight font-bold">{business?.branding?.business_name || business?.name}</h1>
          <div className="text-sm text-muted-foreground mt-1">Here's what's happening on your round today.</div>
        </div>
        <div className="flex gap-2">
          <Link to="/app/route" className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90" data-testid="dashboard-start-route">Start today's route →</Link>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="dashboard-kpis">
        <KpiCard label="Today's jobs" value={loading ? "…" : data.today_jobs} sublabel={loading ? "" : `${data.completed_today} done · ${data.pending_today} left`} icon={ListChecks} testid="kpi-today-jobs" />
        <KpiCard label="Money today" value={loading ? "…" : formatGBP(data.money_today)} tone="success" icon={CircleDollarSign} testid="kpi-money-today" />
        <KpiCard label="Outstanding" value={loading ? "…" : formatGBP(data.outstanding)} tone="warn" icon={Clock} testid="kpi-outstanding" />
        <KpiCard label="Overdue" value={loading ? "…" : formatGBP(data.overdue_amount)} sublabel={loading ? "" : `${data.overdue_count} invoices > 14 days`} tone={loading || !data?.overdue_count ? "default" : "danger"} icon={AlertTriangle} testid="kpi-overdue" />
        <KpiCard label="Completed today" value={loading ? "…" : data.completed_today} tone="success" icon={CalendarCheck} testid="kpi-completed" />
        <KpiCard label="Pending today" value={loading ? "…" : data.pending_today} icon={Clock} testid="kpi-pending" />
        <KpiCard label="Upcoming (7 days)" value={loading ? "…" : data.upcoming_count} icon={CalendarDays} testid="kpi-upcoming" />
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-card p-6" data-testid="today-list-card">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold">Today</h2>
            <Link to="/app/route" className="text-sm text-primary hover:underline">Open route →</Link>
          </div>
          {loading ? null : data.today_list.length === 0 ? (
            <div className="mt-6 text-sm text-muted-foreground">No jobs scheduled for today. Enjoy a quiet one.</div>
          ) : (
            <ul className="mt-4 divide-y divide-border">
              {data.today_list.map((j) => (
                <li key={j.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{j.customer_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{j.customer_address} · {j.customer_postcode}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-sm font-semibold">{formatGBP(j.price)}</div>
                    <StatusBadge status={j.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-6" data-testid="upcoming-card">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold">Upcoming</h2>
            <Link to="/app/calendar" className="text-sm text-primary hover:underline">View calendar →</Link>
          </div>
          {loading ? null : data.upcoming.length === 0 ? (
            <div className="mt-6 text-sm text-muted-foreground">Nothing scheduled in the next 7 days.</div>
          ) : (
            <ul className="mt-4 divide-y divide-border">
              {data.upcoming.map((j) => (
                <li key={j.id} className="py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{j.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{formatUKDate(j.scheduled_date)} · {j.customer_postcode}</div>
                  </div>
                  <div className="text-sm font-semibold">{formatGBP(j.price)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
